import { useCallback, useEffect, useMemo, useState } from 'react'
import * as R from 'remeda'
import invariant from 'tiny-invariant'
import {
  MapDataFieldRefFormDataField,
  MeetingSlotSelectedWithReservation,
  MeetingSlotSelectedWithoutReservation,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { useMutation } from '@tanstack/react-query'
import { useRoomDataContext } from '../chat-data/RoomDataContext'
import { useXMPPClientContext } from '../xmpp-client/XMPPClientContext'
import { TypedEnvelopeOut } from '../xmpp-client/types'
import { usePrepareChat } from '../api-queries/chat-platform-chat'
import { ParsedBlock, SystemSetupPiperAiChat } from '../chat-data/types'
import { getBlocksWithCompositeBlocks } from '../components/compositeBlocks'
import { useNotificationSound } from '../hooks/useNotificationSound'
import { useThrottledBlocks } from './useThrottledBlocks'
import { getPrepareChatData, setPrepareChatData, syncStoredGuestId } from './helpers'

interface Params {
  onBootstrapComplete?: (roomId: string, playbookId: string) => void
  tenantId?: string
  workspaceId?: string
}

export const useMessageFeed = ({ workspaceId, tenantId, onBootstrapComplete }: Params) => {
  const [roomId, setRoomId] = useState<string>()
  const guestId = useMemo(() => {
    if (workspaceId && tenantId) {
      return syncStoredGuestId(workspaceId, tenantId)
    }
  }, [workspaceId, tenantId])

  const { mutateAsync: prepareChatAsync } = usePrepareChat(tenantId ?? '')

  const {
    client: { start, stop, sendEvent, status, loadAffiliations },
  } = useXMPPClientContext()

  const { events } = useRoomDataContext()

  // We get only events from the last session because older events are irrelevant for the UI and we split the message parts into separate messages so we can throttle them separately
  const lastSessionEvents = R.pipe(
    events,
    getLastSessionEvents,
    fixEnterPiperAiId,
    splitMessageEvents
  )

  const { throttledBlocks, isThrottling } = useThrottledBlocks({
    blocks: lastSessionEvents,
  })
  useNotification(`${guestId}_guest`, throttledBlocks)

  const {
    isSessionExpired,
    isWaitingForPiperAiResponse,
    liveChatActive,
    piperAiActive,
    sendingMessage,
  } = getEventsRelatedData(throttledBlocks)

  const blocks = R.pipe(
    throttledBlocks,
    blocks => getRenderableBlocks(blocks, isWaitingForPiperAiResponse),
    // After throttling we merge the message parts into a single message so we can display them grouped
    blocks => getBlocksWithCompositeBlocks(blocks, guestId ?? '')
  )

  const sendLiveEvent = useCallback(
    (content: Extract<TypedEnvelopeOut, { type: 'LiveEnvelope' }>['payload']) => {
      if (roomId) {
        return sendEvent(
          roomId,
          {
            createdAt: new Date().toISOString(),
            type: 'LiveEnvelope',
            payload: content,
            protocolVersion: 1,
          },
          true
        )
      }
    },
    [roomId, sendEvent]
  )

  const sendBotEvent = useCallback(
    (
      content: Extract<TypedEnvelopeOut, { type: 'BotEnvelope' }>['payload'],
      optimisticUpdate?: boolean
    ) => {
      if (roomId) {
        return sendEvent(
          roomId,
          {
            createdAt: new Date().toISOString(),
            type: 'BotEnvelope',
            payload: content,
            protocolVersion: 1,
          },
          optimisticUpdate
        )
      }
    },
    [roomId, sendEvent]
  )

  const pickReplyButton = useCallback(
    (replyIndex: number) => {
      return sendBotEvent({ type: 'ReplyButtonSelected', index: replyIndex }, true)
    },
    [sendBotEvent]
  )

  const selectMeetingSlot = useCallback(
    (data: MeetingSlotSelectedWithReservation | MeetingSlotSelectedWithoutReservation) => {
      return sendBotEvent(data)
    },
    [sendBotEvent]
  )

  const triggerMeetingFallback = useCallback(() => {
    return sendBotEvent({ type: 'MeetingSlotSelectionCancelled' })
  }, [sendBotEvent])

  const submitForm = useCallback(
    (values: MapDataFieldRefFormDataField) => {
      return sendBotEvent({ type: 'FormData', values }, true)
    },
    [sendBotEvent]
  )

  const sendLiveMessage = useCallback(
    (text: string) => {
      return sendLiveEvent({
        type: 'LiveMessage',
        parts: [{ type: 'Text', text }],
      })
    },
    [sendLiveEvent]
  )

  const sendMessageToPiperAi = useCallback(
    (text: string) => {
      return sendBotEvent({ type: 'MessageToPiperAi', value: { content: text } }, true)
    },
    [sendBotEvent]
  )

  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  const { isPending: isLoading, mutateAsync: prepareChat } = useMutation({
    mutationFn: async ({
      tenantId,
      workspaceId,
      guestId,
      playbookId,
    }: {
      guestId: string
      playbookId: string
      tenantId: string
      workspaceId: string
    }) => {
      if (!tenantId || !workspaceId || !guestId) {
        return
      }
      let roomId: string | undefined

      const prepare = async (isRetrying: boolean) => {
        const cachedPrepareChatData = getPrepareChatData(guestId, workspaceId)
        if (cachedPrepareChatData && !isRetrying) {
          return cachedPrepareChatData
        }
        const data = await prepareChatAsync({
          guestId,
          workspaceId,
        })
        setPrepareChatData(guestId, workspaceId, data)
        return data
      }
      await start(tenantId, workspaceId, async isRetrying => {
        const { refreshToken, roomJid, guestJid, authorizationHeader } = await prepare(isRetrying)
        const userAccessToken = window.atob(authorizationHeader.replace('Bearer ', ''))
        const userRefreshToken = refreshToken
          ? window.atob(refreshToken.replace('Bearer ', ''))
          : undefined
        const newRoomId = roomJid.split('@')[0]
        invariant(newRoomId, 'Room ID must be defined')
        roomId = newRoomId
        onBootstrapComplete?.(roomId, playbookId)
        setRoomId(roomId)
        return {
          credentials: {
            username: guestJid,
            token: userAccessToken,
            refreshToken: userRefreshToken,
          },
          roomId,
        }
      })
      if (roomId) {
        await loadAffiliations({ roomId })
      }
    },
  })

  return {
    selectMeetingSlot,
    guestId,
    liveChatActive,
    sendingMessage,
    pickReplyButton,
    submitForm,
    blocks,
    isSessionExpired,
    isThrottling,
    sendLiveMessage,
    prepareChat,
    isLoading,
    status,
    roomId,
    triggerMeetingFallback,
    piperAiActive,
    sendMessageToPiperAi,
    isWaitingForPiperAiResponse,
  }
}

const splitMessageEvents = (events: Array<ParsedBlock>) => {
  const splitEvents: Array<ParsedBlock> = []
  for (const event of events) {
    if (event.type === 'Message') {
      for (const part of event.parts) {
        splitEvents.push({
          ...event,
          parts: [part],
        })
      }
    } else {
      splitEvents.push(event)
    }
  }
  return splitEvents
}

const getLastSessionEvents = (events: Array<ParsedBlock>) => {
  return R.takeLastWhile(events, (block, index) => {
    const isLastBlock = events.length - 1 === index
    return (
      isLastBlock ||
      block.type !== 'SystemMessage' ||
      block.payload.type !== 'ConversationSessionExpired'
    )
  })
}

/**
 * BE sends EnterPiperAiChat before SetupPiperAiChat and because it has a different id than the one
 * sent after SetupPiperAiChat we need to find update the later one to use the same id. Otherwise,
 * the button will remount rerunning the enter animation
 */
const fixEnterPiperAiId = (events: Array<ParsedBlock>) => {
  const setupIndex = R.findLastIndex(
    events,
    (event): event is SystemSetupPiperAiChat =>
      event.type === 'SystemMessage' && event.payload.type === 'SetupPiperAiChat'
  )

  const enterBeforeSetup = R.findLast(
    events,
    (event, index) => (setupIndex === -1 || index < setupIndex) && event.type === 'EnterPiperAiChat'
  )
  const enterAfterSetupIndex = R.findIndex(
    events,
    (event, index) => index !== -1 && index > setupIndex && event.type === 'EnterPiperAiChat'
  )
  const enterAfterSetup = events[enterAfterSetupIndex]

  if (enterAfterSetup && enterBeforeSetup) {
    return R.splice(events, enterAfterSetupIndex, 1, [
      {
        ...enterAfterSetup,
        id: enterBeforeSetup.id,
      },
    ])
  }
  return events
}

const useNotification = (guestId: string | undefined, events: Array<ParsedBlock>) => {
  const [firstMessageId, setFirstMessageId] = useState<string | undefined>(events?.[0]?.id)
  const [lastPlayedMessagesCount, setLastPlayedMessagesCount] = useState(0)
  const { play } = useNotificationSound()

  if (firstMessageId !== events?.[0]?.id) {
    setFirstMessageId(events?.[0]?.id)
    setLastPlayedMessagesCount(events.length)
  }

  const relevantEvents = useMemo(() => {
    return events.filter(event => {
      const isOwnEvent = event.author === guestId && event.type === 'LiveMessage'
      const isEventWorthNotifying = [
        'LiveMessage',
        'Message',
        'ReplyButtons',
        'PiperAiMessage',
        'FillForm',
        'BookMeeting',
      ].includes(event.type)
      return isEventWorthNotifying && !isOwnEvent
    })
  }, [events, guestId])

  if (relevantEvents.length > lastPlayedMessagesCount) {
    play()
    setLastPlayedMessagesCount(relevantEvents.length)
  }
}

const getIsSessionExpired = (lastBlock: ParsedBlock | undefined) => {
  return (
    lastBlock?.type === 'SystemMessage' && lastBlock.payload.type === 'ConversationSessionExpired'
  )
}

export const getEventsRelatedData = (events: Array<ParsedBlock>) => {
  const lastBlock = events.at(-1)
  const liveChatActive =
    lastBlock?.type === 'RouteToRep' ||
    lastBlock?.type === 'StatusChange' ||
    lastBlock?.type === 'LiveMessage' ||
    lastBlock?.type === 'SuggestedTimes' ||
    (lastBlock?.type === 'SystemMessage' &&
      (lastBlock.payload.type === 'SystemConversationReassigned' ||
        lastBlock.payload.type === 'SystemConversationAssigned'))

  const terminateConversationIndex = R.findLastIndex(
    events,
    event => event.type === 'TerminateConversation'
  )

  const lastEnterPiperAiChatIndex = R.findLastIndex(
    events,
    event => event.type === 'EnterPiperAiChat'
  )
  const piperAiActive =
    Boolean(lastEnterPiperAiChatIndex > terminateConversationIndex) && !liveChatActive

  const lastMessageToPiperAiIndex = R.findLastIndex(
    events,
    event => event.type === 'MessageToPiperAi'
  )
  const piperAiSetupIndex = R.findLastIndex(
    events,
    (event): event is SystemSetupPiperAiChat =>
      event.type === 'SystemMessage' && event.payload.type === 'SetupPiperAiChat'
  )

  const isWaitingForAiBeforeSetup = piperAiSetupIndex === -1 && lastMessageToPiperAiIndex !== -1

  const [eventsUntilSetup, eventsAfterSetup] = R.splitAt(events, piperAiSetupIndex + 2) // +2 because we skip the EnterPiperAiChat right after the SetupPiperAiChat too as it's not a response to a message
  const lastAiResponseAfterSetupIndexInEventsAfterSetup = R.findLastIndex(
    eventsAfterSetup,
    event => event.type === 'EnterPiperAiChat' || event.type === 'PiperAiMessage'
  )
  const lastAiResponseAfterSetupIndex =
    lastAiResponseAfterSetupIndexInEventsAfterSetup !== -1
      ? lastAiResponseAfterSetupIndexInEventsAfterSetup + eventsUntilSetup.length
      : -1

  const isWaitingForAiAfterSetup =
    piperAiSetupIndex !== -1 && lastMessageToPiperAiIndex > lastAiResponseAfterSetupIndex

  const isWaitingForPiperAiResponse = Boolean(
    piperAiActive && (isWaitingForAiBeforeSetup || isWaitingForAiAfterSetup)
  )

  const isSessionExpired = getIsSessionExpired(lastBlock)
  const sendingMessage = events.some(event => !event.sent)

  return {
    liveChatActive,
    piperAiActive,
    isWaitingForPiperAiResponse,
    isSessionExpired,
    sendingMessage,
  }
}

const getRenderableBlocks = (blocks: Array<ParsedBlock>, isWaitingForPiperAiResponse: boolean) => {
  return blocks.filter((block, index) => {
    return (
      ((block.type !== 'SystemMessage' ||
        block.payload.type === 'SystemConversationAssigned' ||
        block.payload.type === 'SystemConversationReassigned' ||
        block.payload.type === 'SystemConversationDeadline') &&
        block.type !== 'RouteToRep' &&
        block.type !== 'StatusChange' &&
        block.type !== 'TerminateConversation' &&
        block.type !== 'EnterPiperAiChat') ||
      shouldDisplayEnterPiperAiChat({
        block,
        blocks,
        index,
        isWaitingForPiperAiResponse,
      })
    )
  })
}

const shouldDisplayEnterPiperAiChat = ({
  block,
  blocks,
  index,
  isWaitingForPiperAiResponse,
}: {
  block: ParsedBlock
  blocks: Array<ParsedBlock>
  index: number
  isWaitingForPiperAiResponse: boolean
}) => {
  if (block.type !== 'EnterPiperAiChat') {
    return false
  }
  // Keep blocks with selected shortcuts
  if (block.selected !== undefined) {
    return true
  }
  const hasShortcuts = block.shortcuts && block.shortcuts.length > 0
  const isTheLastBlock = index >= blocks.length - 1
  // Keep blocks that should display shortcuts at the bottom
  return hasShortcuts && isTheLastBlock && !isWaitingForPiperAiResponse
}
